/**
 * The grating types a Vorgang can carry (spec 2026-10-23 R-5, R-7, R-8) — in
 * the words and widths the screens choose them by, at the Eingang and in the
 * planning head.
 *
 * The catalogue itself is the server's (`planning.layout.GRATING_SPECS`); it
 * travels with every plan answer (`gratingSpecs`), and the planning head
 * reads its widths from there. The Eingang has no plan answer to read — a
 * mail's Vorgang has no Stand yet — so the three are mirrored here, and
 * `gratingTypes.test.ts` holds the mirror to a recorded answer of the
 * server's. The words on the wire are the contract's (`GratingTypeIn`).
 *
 * In `lib/` and not under one feature: the Eingang, the customer block and
 * the planning board all name them, and features do not import each other
 * (R-W1).
 */
import { ProblemError, type GratingTypeWord } from "@/api";

import { formatMm } from "./format";

export interface GratingTypeEntry {
  type: GratingTypeWord;
  /** The trade name, as the catalogue says it („Barfußrost“). */
  label: string;
  widthMm: number;
  /**
   * Max has no price rule for it (D-19): choosable, and said so — the
   * calculation ends with a named reason, never with an estimate (R-8).
   */
  unpriced: boolean;
}

/** In the order a person reads them: the standard first. */
export const GRATING_TYPES: readonly GratingTypeEntry[] = [
  { type: "pressrost", label: "Pressrost", widthMm: 300, unpriced: false },
  { type: "barfuss", label: "Barfußrost", widthMm: 288, unpriced: false },
  { type: "laengsstab", label: "Längsstabrost", widthMm: 288, unpriced: true },
];

/** What R-8 adds to Längsstab wherever it can be chosen. */
export const UNPRICED_NOTE = "ohne Preisregel";

export function isGratingTypeWord(value: string | null | undefined): value is GratingTypeWord {
  return GRATING_TYPES.some((entry) => entry.type === value);
}

/** The catalogue entry of a stored word — `undefined` for one the catalogue does not know. */
export function gratingTypeEntry(type: string | null | undefined): GratingTypeEntry | undefined {
  return GRATING_TYPES.find((entry) => entry.type === type);
}

/** „Barfußrost“ — a word the catalogue does not know keeps its own spelling rather than a made-up name. */
export function gratingTypeLabel(type: string): string {
  return gratingTypeEntry(type)?.label ?? type;
}

/**
 * A plan answer's catalogue entry, as far as this needs it — the label is
 * optional because a test's answer may leave it out.
 */
export interface CatalogueSpec {
  type: string;
  widthMm: number;
  label?: string;
}

/**
 * The three as a select offers them: „Pressrost · 300 mm“, „Barfußrost ·
 * 288 mm“, „Längsstabrost · 288 mm · ohne Preisregel“. The width beside the
 * name because it is the figure the choice changes; read off the plan
 * answer's catalogue where there is one, the mirror elsewhere.
 */
export function gratingTypeOptions(catalogue: readonly CatalogueSpec[] = []): { value: GratingTypeWord; label: string }[] {
  return GRATING_TYPES.map((entry) => {
    const spec = catalogue.find((each) => each.type === entry.type);
    const parts = [spec?.label ?? entry.label, formatMm(spec?.widthMm ?? entry.widthMm)];
    if (entry.unpriced) parts.push(UNPRICED_NOTE);
    return { value: entry.type, label: parts.join(" · ") };
  });
}

/** The width a type lays at — the plan answer's where it names one, the mirror's otherwise. */
export function gratingTypeWidthMm(type: string, catalogue: readonly CatalogueSpec[] = []): number | null {
  return catalogue.find((each) => each.type === type)?.widthMm ?? gratingTypeEntry(type)?.widthMm ?? null;
}

/**
 * What a change of type does to the gratings on the board (R-7, R-10), in
 * the words of the question before it: „Die Roste werden 288 statt 300 mm
 * breit; die Kalkulation wird neu.“
 *
 * `following` — some laid grating has no type of its own and follows the
 * Vorgang; `ownTyped` — some carries its own and stays as it is (R-10). Two
 * types of one width (Barfuß and Längsstab) change no width, and the
 * sentence says that rather than „288 statt 288“.
 */
export function typeChangeSentence({
  fromWidthMm,
  toWidthMm,
  following,
  ownTyped,
}: {
  fromWidthMm: number | null;
  toWidthMm: number | null;
  following: boolean;
  ownTyped: boolean;
}): string {
  const parts: string[] = [];
  if (following) {
    if (fromWidthMm !== null && toWidthMm !== null && fromWidthMm !== toWidthMm) {
      parts.push(`Die Roste werden ${toWidthMm} statt ${fromWidthMm} mm breit; die Kalkulation wird neu.`);
    } else if (toWidthMm !== null) {
      parts.push(`Die Roste bleiben ${toWidthMm} mm breit; die Kalkulation wird neu.`);
    } else {
      parts.push("Die Kalkulation wird neu.");
    }
  } else {
    parts.push("Die Kalkulation wird neu.");
  }
  if (ownTyped) parts.push("Roste mit eigenem Rosttyp bleiben, wie sie sind.");
  return parts.join(" ");
}

/**
 * What lies on the board, as far as a change of type touches it (R-7, R-10,
 * Ruling G-1) — the planning head and the Eingang ask with it alike.
 */
export interface LaidGratings {
  /** Some laid grating has no type of its own and follows the Vorgang. */
  following: boolean;
  /** Some carries its own type and stays as it is (R-10). */
  ownTyped: boolean;
  /**
   * Brackets a person gave an article by hand (`consoleOverrides`). An
   * article is tied to a width, and a change of type keeps it: it is a
   * person's decision (A-014), so the question names it and asks to check.
   */
  handSetConsoles: number;
  /** Gratings whose blank a person typed (`blankLengthMm`) — they stay too, and are named alike. */
  typedBlanks: number;
}

/** A placement as far as {@link laidGratingsOf} reads it — the board's and the wire's alike. */
interface LaidPiece {
  id: string;
  kind: string;
  gratingType?: string | null;
  blankLengthMm?: number | null;
}

/**
 * {@link LaidGratings} from a planning state — the board's store or a Stand
 * read off the wire (`GET …/plan`'s `planningState`). `null` where no grating
 * lies: a change then has nothing on the board to say anything about.
 *
 * An override is counted only while the placement it is addressed to
 * (`"<placementId>:<spot>"`) still lies — one left behind by a grating that
 * was taken away prices no bracket, and naming it would ask to check a
 * console that is not there.
 */
export function laidGratingsOf(
  placements: readonly LaidPiece[],
  consoleOverrides: Readonly<Record<string, string>> | null | undefined,
): LaidGratings | null {
  const gratings = placements.filter((each) => each.kind === "grating");
  if (gratings.length === 0) return null;
  const lying = new Set(placements.map((each) => each.id));
  const handSetConsoles = Object.keys(consoleOverrides ?? {}).filter((key) => {
    const cut = key.lastIndexOf(":");
    return lying.has(cut < 0 ? key : key.slice(0, cut));
  }).length;
  return {
    following: gratings.some((each) => !each.gratingType),
    ownTyped: gratings.some((each) => !!each.gratingType),
    handSetConsoles,
    typedBlanks: gratings.filter((each) => each.blankLengthMm != null).length,
  };
}

/**
 * What a change of type leaves standing that a person set by hand (Ruling
 * G-1): „2 Konsolen hast du von Hand gesetzt — sie bleiben; prüf sie nach
 * dem Wechsel.“ — and the same for typed blanks. Empty where there is none.
 * Said in the question before the change and again in the sentence after it.
 */
export function handSetSentence({ handSetConsoles, typedBlanks }: Pick<LaidGratings, "handSetConsoles" | "typedBlanks">): string {
  const parts: string[] = [];
  if (handSetConsoles === 1) {
    parts.push("1 Konsole hast du von Hand gesetzt — sie bleibt; prüf sie nach dem Wechsel.");
  } else if (handSetConsoles > 1) {
    parts.push(`${handSetConsoles} Konsolen hast du von Hand gesetzt — sie bleiben; prüf sie nach dem Wechsel.`);
  }
  if (typedBlanks === 1) {
    parts.push("1 Rohlinglänge hast du von Hand getippt — sie bleibt; prüf sie nach dem Wechsel.");
  } else if (typedBlanks > 1) {
    parts.push(`${typedBlanks} Rohlinglängen hast du von Hand getippt — sie bleiben; prüf sie nach dem Wechsel.`);
  }
  return parts.join(" ");
}

/**
 * A refused change, in one form on both screens: „Rosttyp nicht geändert —
 * {the server's sentence}“ — the planning head's and the Eingang's alike.
 * The server's sentence where there is one (`userMessage`), the failure's
 * own otherwise.
 */
export function gratingTypeRefusal(cause: unknown): string {
  const sentence =
    cause instanceof ProblemError
      ? cause.userMessage
      : cause instanceof Error && cause.message !== ""
        ? cause.message
        : "Der Server ist nicht erreichbar.";
  return `Rosttyp nicht geändert — ${sentence}`;
}
